import React, { useState } from 'react';
import { LockIcon, DownloadIcon, ShieldCheckIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CTAButton } from './CTAButton';
type PlanKey = 'basico' | 'completo';
const PLANS: {
  key: PlanKey;
  icon: string;
  title: string;
  subtitle: string;
  oldPrice: string;
  price: string;
  href: string;
  highlighted?: boolean;
  badges?: string[];
  details: string[];
  extras?: {
    icon: string;
    label: string;
    color: string;
  }[];
}[] = [{
  key: 'basico',
  icon: '📘',
  title: 'PACK BÁSICO',
  subtitle: '6 guías + 4 bonos',
  oldPrice: '$126.000',
  price: '$19.990',
  href: 'https://checkout.ars.descargatop.com/cart/47490248802562:1',
  details: ['Tomo 1 — Fundamentos CALMA', 'Tomo 2 — Tablero de Rutinas Mágicas', 'Tomo 3 — Rabietas con Calma', 'Tomo 4 — Diccionario de las Emociones', 'Tomo 5 — Disciplina Sin Gritos', 'Tomo 6 — Diario de la Madre Calmada', '4 Bonos exclusivos']
}, {
  key: 'completo',
  icon: '🏆',
  title: 'PACK COMPLETO',
  subtitle: '6 guías + 4 bonos + 2 exclusivos',
  oldPrice: '$180.000',
  price: '$29.990',
  href: 'https://checkout.ars.descargatop.com/cart/47490249588994:1',
  highlighted: true,
  badges: ['⭐ Más elegido', '🔥 Mejor valor'],
  details: ['Tomo 1 — Fundamentos CALMA', 'Tomo 2 — Tablero de Rutinas Mágicas', 'Tomo 3 — Rabietas con Calma', 'Tomo 4 — Diccionario de las Emociones', 'Tomo 5 — Disciplina Sin Gritos', 'Tomo 6 — Diario de la Madre Calmada', '4 Bonos exclusivos'],
  extras: [{
    icon: '⚡',
    label: 'Acelerador 21 Días',
    color: 'text-yellow-400'
  }, {
    icon: '🚑',
    label: 'Botiquín de Emergencia Emocional',
    color: 'text-red-400'
  }]
}];
export function PricingSection() {
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('completo');
  const currentPlan = PLANS.find(p => p.key === selectedPlan)!;
  return <section id="pricing" className="py-16 px-4 bg-slate-950 scroll-mt-10" data-id="element-173">
      <div className="max-w-xl mx-auto" data-id="element-174">
        <div className="text-center mb-6" data-id="element-175">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3" data-id="element-176">
            🛒 ELEGÍ TU PLAN Y EMPEZÁ HOY
          </h2>
          <p className="text-base text-slate-400 font-medium italic" data-id="element-177">
            Descarga inmediata. Acceso de por vida. El cambio no espera.
          </p>
        </div>

        <p className="text-center text-yellow-400 font-bold text-sm mb-6" data-id="element-178">
          👇 Elegí tu plan y dale a COMPRAR AHORA 👇
        </p>

        <div className="space-y-3 mb-8" data-id="element-179">
          {PLANS.map(plan => {
          const isSelected = selectedPlan === plan.key;
          const isHighlighted = plan.highlighted;
          return <div key={plan.key} onClick={() => setSelectedPlan(plan.key)} className={`rounded-xl cursor-pointer transition-all duration-300 overflow-hidden ${isHighlighted && isSelected ? 'border-2 border-yellow-400 bg-slate-800 shadow-[0_0_20px_rgba(250,204,21,0.15)]' : isHighlighted ? 'border border-yellow-400/50 bg-slate-900 hover:border-yellow-400/80' : isSelected ? 'border border-slate-600 bg-slate-800 shadow-lg' : 'border border-slate-800 bg-slate-900 hover:border-slate-700'}`} data-id="element-180">
                {/* Header — always visible */}
                <div className="p-4 flex items-center gap-3" data-id="element-181">
                  <div className="flex-shrink-0" data-id="element-182">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? isHighlighted ? 'border-yellow-400' : 'border-white' : 'border-slate-600'}`} data-id="element-183">
                      {isSelected && <div className={`w-2.5 h-2.5 rounded-full ${isHighlighted ? 'bg-yellow-400' : 'bg-white'}`} data-id="element-184" />}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-185">
                    <div className="flex items-center gap-2" data-id="element-186">
                      <h3 className="text-base font-black text-white" data-id="element-187">
                        {plan.icon} {plan.title}
                      </h3>
                    </div>
                    <p className="text-slate-400 text-xs mt-0.5" data-id="element-188">
                      {plan.subtitle}
                    </p>
                    {isHighlighted && <div className="flex flex-wrap gap-1.5 mt-1.5" data-id="element-189">
                        {plan.badges?.map((badge, i) => <span key={i} className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${i === 0 ? 'bg-yellow-400 text-yellow-900' : 'bg-red-500 text-white'}`} data-id="element-190">
                            {badge}
                          </span>)}
                      </div>}
                  </div>

                  <div className="text-right flex-shrink-0 flex items-center gap-3" data-id="element-191">
                    <div data-id="element-192">
                      <span className="text-slate-500 line-through text-xs block" data-id="element-193">
                        {plan.oldPrice}
                      </span>
                      <span className={`font-black ${isHighlighted ? 'text-2xl text-white' : 'text-xl text-white'}`} data-id="element-194">
                        {plan.price}
                      </span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${isSelected ? 'rotate-180' : ''}`} data-id="element-195" />
                  </div>
                </div>

                {/* Expandable details */}
                <AnimatePresence data-id="element-196">
                  {isSelected && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-197">
                      <div className="px-4 pb-4 pt-1 border-t border-slate-700/50 ml-8" data-id="element-198">
                        <ul className="space-y-1.5 mt-2" data-id="element-199">
                          {plan.details.map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-200">
                              <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" data-id="element-201" />
                              <span className="text-slate-400 text-xs" data-id="element-202">
                                {item}
                              </span>
                            </li>)}
                        </ul>
                        {plan.extras && <div className="mt-3 space-y-1.5" data-id="element-203">
                            {plan.extras.map((extra, i) => <div key={i} className={`flex items-start gap-2 ${extra.color} font-bold text-xs`} data-id="element-204">
                                <span data-id="element-205">+</span>
                                <span className={`${extra.color.replace('text-', 'bg-')}/10 px-2 py-0.5 rounded`} data-id="element-206">
                                  {extra.icon} {extra.label}
                                </span>
                              </div>)}
                          </div>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        <p className="text-center text-slate-400 text-xs italic mb-4" data-id="element-207">
          🔥 El 80% de nuestros clientes elige el Pack Completo. Es el que más
          valor tiene por su precio.
        </p>

        {/* CTA Button */}
        <CTAButton text="👉 COMPRAR AHORA" href={currentPlan.href} subtext="Descarga inmediata • Garantía de 7 días" data-id="element-208" />

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-6 text-xs font-bold text-slate-500" data-id="element-209">
          <div className="flex items-center gap-1.5" data-id="element-210">
            <LockIcon className="w-3.5 h-3.5 text-green-500" data-id="element-211" />
            <span data-id="element-212">Pago seguro</span>
          </div>
          <span className="text-slate-700" data-id="element-213">•</span>
          <div className="flex items-center gap-1.5" data-id="element-214">
            <DownloadIcon className="w-3.5 h-3.5 text-green-500" data-id="element-215" />
            <span data-id="element-216">Descarga inmediata</span>
          </div>
          <span className="text-slate-700" data-id="element-217">•</span>
          <div className="flex items-center gap-1.5" data-id="element-218">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-219" />
            <span data-id="element-220">Garantía 7 días</span>
          </div>
        </div>
      </div>
    </section>;
}